import React, { useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import ApartmentIcon from "@mui/icons-material/Apartment";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import { Button, useNotify, useRefresh } from "react-admin";
import dataProvider from "server/dataProvider";
import { dormitoryRequests } from "api/dormitoryApi";
import { useNotifyError } from "utils/useNotifyError";
import { useSourceRoomLabel } from "../Partials/useSourceRoomLabel";
import { DormitoryRoomStatuses } from "utils/constants";
import ModalTitle, {
  withModalSx,
  modalButtonSx,
  modalCancelButtonSx,
  modalConfirmButtonSx,
  modernUiProps,
} from "server/components/Modals/ModalTitle";

// StudentRightsStatus, numbered from 1 in the backend: Active/Interrupted/Suspended.
const STUDENT_RIGHTS_STATUS_LABELS = {
  1: "Активен",
  2: "Прекъснал",
  3: "Отстранен",
};

// Uses custom Dialog instead of ConfirmModal because it supports dual distinct actions (Approve with room allocation vs Reject) with independent validation and async states.
const ReviewApplicationModal = ({ record, isExternal = false }) => {
  const notify = useNotify();
  const notifyError = useNotifyError();
  const refresh = useRefresh();
  // For a transfer application the assigned room is the destination; show where the student currently
  // lives so staff pick a destination knowing the source room.
  const [open, setOpen] = useState(false);
  const sourceRoom = useSourceRoomLabel(record?.isTransfer ? record?.sourceAccommodationId : null, open);
  const [loading, setLoading] = useState(false);

  // Buildings and rooms state
  const [buildings, setBuildings] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [loadingBuildings, setLoadingBuildings] = useState(false);

  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [loadingRooms, setLoadingRooms] = useState(false);

  // The paged row (record) only carries the identity fields shown in the table. Email, phone,
  // speciality, course, student-rights status and the applicant's room preferences are
  // review-only and live on the GetById detail.
  const [details, setDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  // A detail read that failed is not the same as one that returned canReview: false, but neither may
  // enable processing, so both land here and both disable the actions.
  const [detailsFailed, setDetailsFailed] = useState(false);

  const handleOpen = async e => {
    e?.stopPropagation();
    setOpen(true);
    setSelectedBuilding(null);
    setSelectedRoom(null);
    setRooms([]);
    setDetails(null);
    setDetailsFailed(false);

    const campaignId = record?.campaignId;
    const applicationId = record?.applicationId || record?.id;

    setLoadingBuildings(true);
    setLoadingDetails(true);
    try {
      const [buildingsResult, detailsResult] = await Promise.allSettled([
        dataProvider.customRequest(dormitoryRequests.getBuildingsSelect(), null, "GET"),
        campaignId
          ? dataProvider.customRequest(dormitoryRequests.getCampaignApplicationById(campaignId, applicationId), null, "GET")
          : Promise.resolve(null),
      ]);

      setBuildings(
        buildingsResult.status === "fulfilled" && Array.isArray(buildingsResult.value?.data)
          ? buildingsResult.value.data
          : [],
      );

      if (detailsResult.status === "fulfilled" && detailsResult.value) {
        setDetails(detailsResult.value.data);
      } else if (detailsResult.status === "rejected") {
        setDetailsFailed(true);
        notifyError(detailsResult.reason);
      } else {
        // No campaign id to read the application back with - nothing to re-evaluate against.
        setDetailsFailed(true);
      }
    } finally {
      setLoadingBuildings(false);
      setLoadingDetails(false);
    }
  };

  const handleClose = () => {
    if (!loading) setOpen(false);
  };

  // When building changes, fetch free rooms for that building
  const handleBuildingChange = async (event, newBuilding) => {
    setSelectedBuilding(newBuilding);
    setSelectedRoom(null);
    setRooms([]);

    if (newBuilding?.id) {
      setLoadingRooms(true);
      try {
        const { data } = await dataProvider.customRequest(
          dormitoryRequests.getBuildingRooms(newBuilding.id),
          null,
          "GET",
        );
        if (Array.isArray(data)) {
          // Filter free and active rooms
          const freeRooms = data.filter(
            r => r.status === DormitoryRoomStatuses.Active && (r.capacity - (r.occupiedBeds || 0)) > 0,
          );
          setRooms(freeRooms);
        } else {
          setRooms([]);
        }
      } catch (err) {
        setRooms([]);
        notifyError(err);
      } finally {
        setLoadingRooms(false);
      }
    }
  };

  // The row that opened the dialog may be minutes old - the campaign can have been cancelled since.
  // Eligibility therefore comes from the detail just read, never from the row and never from the tab,
  // and stays false while that read is in flight or after it failed.
  const reviewAllowed = !loadingDetails && !detailsFailed && details?.canReview === true;

  const handleApprove = async () => {
    if (!reviewAllowed) return;
    if (!selectedBuilding) {
      notify("Моля, изберете блок за настаняване.", { type: "warning" });
      return;
    }
    if (!selectedRoom) {
      notify("Моля, изберете свободна стая в избрания блок.", { type: "warning" });
      return;
    }

    const campaignId = record?.campaignId;
    const applicationId = record?.applicationId || record?.id;

    if (!campaignId) {
      notify("Липсва идентификатор на кампания за тази молба.", { type: "error" });
      return;
    }

    setLoading(true);
    try {
      await dataProvider.customRequest(
        dormitoryRequests.allocateCampaignApplication(campaignId, applicationId),
        { roomId: selectedRoom.id },
        "PUT",
      );

      notify(
        `Молбата е одобрена успешно и студентът е разпределен в Блок ${selectedBuilding.name}, Стая ${selectedRoom.roomNumber}!`,
        { type: "success" },
      );
      setOpen(false);
      refresh();
    } catch (err) {
      notifyError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!reviewAllowed) return;

    const campaignId = record?.campaignId;
    const applicationId = record?.applicationId || record?.id;

    if (!campaignId) {
      notify("Липсва идентификатор на кампания за тази молба.", { type: "error" });
      return;
    }

    setLoading(true);
    try {
      await dataProvider.customRequest(
        dormitoryRequests.rejectCampaignApplication(campaignId, applicationId),
        {},
        "PUT",
      );

      notify("Молбата е отказана успешно.", { type: "info" });
      setOpen(false);
      refresh();
    } catch (err) {
      notifyError(err);
    } finally {
      setLoading(false);
    }
  };

  const fullName = record?.studentFullName || "—";

  return (
    <>
      <Button
        startIcon={<AssignmentTurnedInIcon />}
        label="Обработи"
        onClick={handleOpen}
      />

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" sx={withModalSx()}>
        <ModalTitle
          icon={<AssignmentTurnedInIcon />}
          // Old look: the default DialogTitle, no bar.
          legacySx={{ bgcolor: "transparent", color: "inherit", fontWeight: 500 }}
          title={<span>Разглеждане на молба {record?.applicationId || record?.id ? `№ ${record.applicationId || record.id}` : ""}</span>}
        />

        <DialogContent dividers sx={{ p: 3 }}>
          <Grid container spacing={2}>
            {/* 1. Студентски данни */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "primary.main", mb: 1 }}>
                Данни на кандидата
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Имена</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{fullName}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">ЕГН / ЛНЧ</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{record?.personId || "—"}</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Имейл</Typography>
              <Typography variant="body2">{loadingDetails ? "…" : details?.email || "—"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Телефон</Typography>
              <Typography variant="body2">{loadingDetails ? "…" : details?.contactPhone || "—"}</Typography>
            </Grid>

            {!isExternal && (
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Факултетен №</Typography>
                <Typography variant="body2">{record?.facultyNumber || "—"}</Typography>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Специалност {isExternal && "(по избор)"}</Typography>
              <Typography variant="body2">{loadingDetails ? "…" : details?.specialityNameBg || "—"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Курс {isExternal && "(по избор)"}</Typography>
              <Typography variant="body2">{loadingDetails ? "…" : details?.course || "—"}</Typography>
            </Grid>

            {!isExternal && (
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Студентски статус</Typography>
                <Typography variant="body2">
                  {loadingDetails ? "…" : STUDENT_RIGHTS_STATUS_LABELS[details?.studentRightsStatus] || "—"}
                </Typography>
              </Grid>
            )}

            {record?.isTransfer && (
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Текуща стая (преместване)</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{sourceRoom || "…"}</Typography>
              </Grid>
            )}

            <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

            {/* 2. Предпочитания на кандидата — подадени от студента при кандидатстването, не влияят на разпределението по-долу */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "primary.main", mb: 1 }}>
                Предпочитания на кандидата
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">Предпочитан блок</Typography>
              <Typography variant="body2">
                {loadingDetails ? "…" : details?.preferredBuildingResidentialNumber || "—"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">Предпочитан тип стая</Typography>
              <Typography variant="body2">
                {loadingDetails ? "…" : details?.preferredRoomTypeDescriptionBg || "—"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">Предпочитана стая</Typography>
              <Typography variant="body2">
                {loadingDetails ? "…" : details?.preferredRoomNumber || "—"}
              </Typography>
            </Grid>
            <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

            {!loadingDetails && !reviewAllowed && (
              <Grid item xs={12}>
                <Typography variant="body2" color="error">
                  {detailsFailed
                    ? "Молбата не може да бъде прочетена в момента, затова обработката е недостъпна. Данните по-горе са само за преглед."
                    : "Молбата не подлежи на обработка - кампанията или статусът ѝ вече не го позволяват. Данните по-горе остават достъпни за преглед."}
                </Typography>
              </Grid>
            )}

            {/* 3. Избор на блок и свободна стая (Typeable Autocomplete с autofill) */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "primary.main", mb: 1 }}>
                {record?.isTransfer
                  ? "Разпределение на нова стая (при одобряване на преместването)"
                  : "Разпределение на стая (при одобряване)"}
              </Typography>
            </Grid>

            {/* Autocomplete за Блок */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={buildings}
                getOptionLabel={option => (typeof option === "string" ? option : option?.name || `Блок #${option?.id}`)}
                value={selectedBuilding}
                onChange={handleBuildingChange}
                loading={loadingBuildings}
                disabled={!reviewAllowed}
                isOptionEqualToValue={(option, val) => option?.id === val?.id}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Изберете или напишете блок"
                    placeholder="Напр. A1, B1..."
                    size="small"
                    required
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <ApartmentIcon color="action" sx={{ mr: 1, fontSize: 20 }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <>
                          {loadingBuildings ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {/* Autocomplete за Стая (появява се само след избор на блок) */}
            {selectedBuilding && (
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={rooms}
                  getOptionLabel={option => {
                    if (typeof option === "string") return option;
                    const freeCount = option.capacity - (option.occupiedBeds || 0);
                    return `Стая ${option.roomNumber} (${option.typeDescriptionBg || "Стандартна"}, Етаж ${option.floor} — свободни места: ${freeCount})`;
                  }}
                  value={selectedRoom}
                  onChange={(_, newRoom) => setSelectedRoom(newRoom)}
                  loading={loadingRooms}
                  disabled={!reviewAllowed}
                  isOptionEqualToValue={(option, val) => option?.id === val?.id}
                  noOptionsText={loadingRooms ? "Зареждане на стаи..." : "Няма свободни стаи в този блок"}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Изберете или напишете свободна стая"
                      placeholder="Напр. 101, 103..."
                      size="small"
                      required
                      helperText={
                        rooms.length > 0
                          ? `Намерени свободни стаи в ${selectedBuilding.name}: ${rooms.length}`
                          : "В този блок няма свободни легла."
                      }
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <MeetingRoomIcon color="action" sx={{ mr: 1, fontSize: 20 }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                        endAdornment: (
                          <>
                            {loadingRooms ? <CircularProgress color="inherit" size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2, px: 3, justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
          <Button
            label="Затвори"
            onClick={handleClose}
            startIcon={<CloseIcon />}
            disabled={loading}
            {...modernUiProps({ variant: "outlined", color: "error" })}
            sx={modalCancelButtonSx}
          />
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              label="Откажи молбата"
              onClick={handleReject}
              startIcon={<CancelIcon />}
              {...modernUiProps({ variant: "contained", color: "error" })}
              sx={modalButtonSx}
              disabled={loading || !reviewAllowed}
            />
            <Button
              label="Одобри молбата"
              onClick={handleApprove}
              startIcon={<CheckCircleIcon />}
              {...modernUiProps({ variant: "contained" })}
              sx={modalConfirmButtonSx}
              disabled={!selectedBuilding || !selectedRoom || loading || !reviewAllowed}
            />
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReviewApplicationModal;
